import { useEffect, useState, useRef, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { getDownloadSettings, submitRegistration } from '@/lib/cms';

interface RegistrationModalProps {
  open: boolean;
  onClose: () => void;
}

const PRODUCTS = [
  '蚂蚁量化交易系统',
  '智能风控平台',
  '策略回测引擎',
  '行情数据终端',
  'API数据服务',
];

const FALLBACK_WECHAT_QR_URL = 'https://readdy.ai/api/search-image?query=Clean%20modern%20WeChat%20QR%20code%20card%20design%20with%20subtle%20gold%20accents%20on%20white%20background%2C%20minimal%20WeChat%20green%20branding%20elements%2C%20simple%20clean%20grid%20pattern%20in%20center%2C%20professional%20corporate%20style%2C%20soft%20shadow%20and%20rounded%20corners%2C%20tech%20finance%20aesthetic%2C%20no%20text%20just%20abstract%20QR%20pattern&width=400&height=400&seq=wechat-qr-contact&orientation=squarish';

export default function RegistrationModal({ open, onClose }: RegistrationModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!open) return;
    getDownloadSettings()
      .then((result) => setWechatQrUrl(result.item.wechatQrUrl || ''))
      .catch(() => setWechatQrUrl(''));
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    setSubmitting(true);

    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      await submitRegistration({
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        company: formData.get('company'),
        products: selectedProducts,
        message: formData.get('message'),
      });
      setSuccess(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : '网络错误，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSuccess(false);
      setError('');
      setSelectedProducts([]);
      onClose();
    }
  };

  const toggleProduct = (product: string) => {
    setSelectedProducts((prev) =>
      prev.includes(product) ? prev.filter((p) => p !== product) : [...prev, product],
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background-950/60 backdrop-blur-md"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-2xl border border-background-300/40 shadow-[0_0_60px_rgba(200,160,80,0.1)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-background-200/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-accent-500/12 flex items-center justify-center">
              <i className="ri-user-add-line text-accent-500 text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-semibold font-heading text-foreground-950">
                报名使用产品
              </h2>
              <p className="text-xs text-foreground-600 mt-0.5">
                留下信息，我们将尽快为您开通试用
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-100 hover:bg-background-200/60 transition-colors disabled:opacity-30 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        {success ? (
          <div className="px-6 py-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/12 flex items-center justify-center mb-5">
              <i className="ri-check-line text-emerald-400 text-3xl"></i>
            </div>
            <h3 className="text-lg font-semibold font-heading text-foreground-950 mb-2">
              报名成功！
            </h3>
            <p className="text-sm text-foreground-600 max-w-xs mb-8">
              我们已收到您的申请，工作人员将尽快通过邮件或电话与您联系。
            </p>

            {/* QR Code Section */}
            <div className="w-full border-t border-background-200/60 pt-7">
              <p className="text-sm font-medium text-foreground-700 mb-4">
                扫码添加客服，优先处理您的申请
              </p>
              <div className="inline-flex flex-col items-center gap-3">
                <div className="animate-qr-float">
                  <div className="w-40 h-40 rounded-2xl overflow-hidden border-2 border-background-200/60 bg-background-100 p-2">
                    <img
                      src={wechatQrUrl || FALLBACK_WECHAT_QR_URL}
                      alt="扫码添加客服微信"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-wechat-line text-emerald-400 text-sm"></i>
                  <span>微信扫一扫，添加专属客服</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="mt-7 px-6 py-2.5 rounded-full bg-accent-500 text-background-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              关闭
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-readdy-form
            className="px-6 py-5 flex flex-col gap-4"
          >
            {/* Name */}
            <div>
              <label
                htmlFor="reg-name"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                姓名 <span className="text-accent-500">*</span>
              </label>
              <input
                id="reg-name"
                name="name"
                type="text"
                required
                placeholder="请输入您的姓名"
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-300/60 bg-background-100/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-400/15 transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="reg-email"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                邮箱 <span className="text-accent-500">*</span>
              </label>
              <input
                id="reg-email"
                name="email"
                type="email"
                required
                placeholder="请输入您的邮箱地址"
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-300/60 bg-background-100/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-400/15 transition-all"
              />
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="reg-phone"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                手机号码 <span className="text-accent-500">*</span>
              </label>
              <input
                id="reg-phone"
                name="phone"
                type="tel"
                required
                placeholder="请输入您的手机号码"
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-300/60 bg-background-100/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-400/15 transition-all"
              />
            </div>

            {/* Company */}
            <div>
              <label
                htmlFor="reg-company"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                公司/机构
              </label>
              <input
                id="reg-company"
                name="company"
                type="text"
                placeholder="请输入您的公司或机构名称"
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-300/60 bg-background-100/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-400/15 transition-all"
              />
            </div>

            {/* Products */}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                意向产品 <span className="text-foreground-500 text-xs font-normal">（可多选）</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {PRODUCTS.map((product) => {
                  const isSelected = selectedProducts.includes(product);
                  return (
                    <button
                      key={product}
                      type="button"
                      onClick={() => toggleProduct(product)}
                      className={`px-3.5 py-2 text-sm rounded-full border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        isSelected
                          ? 'bg-accent-500/15 text-accent-500 border-accent-500/40'
                          : 'bg-background-100/50 text-foreground-600 border-background-300/50 hover:border-accent-400/30 hover:text-foreground-400'
                      }`}
                    >
                      {isSelected && <i className="ri-check-line mr-1.5 text-xs"></i>}
                      {product}
                    </button>
                  );
                })}
              </div>
              {/* Hidden inputs for form submission */}
              {selectedProducts.map((product) => (
                <input key={product} type="hidden" name="product" value={product} />
              ))}
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="reg-message"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                留言
              </label>
              <textarea
                id="reg-message"
                name="message"
                rows={3}
                maxLength={500}
                placeholder="如有其他需求或问题，请在此留言（选填，最多500字）"
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-background-300/60 bg-background-100/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-400/15 transition-all resize-none"
              ></textarea>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/8 border border-red-500/15 text-red-400 text-sm">
                <i className="ri-error-warning-line flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-full bg-accent-500 text-background-950 text-sm font-semibold hover:bg-accent-400 transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </>
              ) : (
                <>
                  提交申请
                  <i className="ri-arrow-right-line text-sm"></i>
                </>
              )}
            </button>

            <p className="text-xs text-foreground-500 text-center -mt-1">
              提交即表示您同意我们通过邮件或电话与您联系
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
